export function mdToHtml(source: string): string {
    const codeBlocks: string[] = [];

    // Extract fenced code blocks first so their content is not transformed
    let s = source.replace(/```([^\n]*)\n([\s\S]*?)```/gm, (_, lang, code) => {
        const escaped = code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const langClass = lang.trim() ? ` class="lang-${lang.trim()}"` : "";
        const i = codeBlocks.length;
        codeBlocks.push(`<pre><code${langClass}>${escaped}</code></pre>`);
        return `\x00CB${i}\x00`;
    });

    // HTML-escape remaining text
    s = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    // Inline code
    s = s.replace(/`([^`\n]+)`/g, "<code>$1</code>");

    // Images (before links)
    s = s.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, '<img alt="$1" src="$2" style="max-width:100%" />');

    // Links
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Headers
    s = s
        .replace(/^###### (.+)$/gm, "<h6>$1</h6>")
        .replace(/^##### (.+)$/gm, "<h5>$1</h5>")
        .replace(/^#### (.+)$/gm, "<h4>$1</h4>")
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/^# (.+)$/gm, "<h1>$1</h1>");

    // Blockquotes (&gt; is the HTML-escaped >)
    s = s.replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>");

    // Bold + italic combinations
    s = s
        .replace(/\*\*\*([^*\n]+)\*\*\*/g, "<strong><em>$1</em></strong>")
        .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*\n]+)\*/g, "<em>$1</em>")
        .replace(/~~([^~\n]+)~~/g, "<del>$1</del>");

    // Horizontal rules
    s = s.replace(/^---+$/gm, "<hr />");

    // Tables: header row | separator row | data rows
    s = s.replace(
        /^(\|[^\n]+\|[ \t]*)\n(\|[ \t]*[-:]+[-| \t:]*\|[ \t]*)((?:\n\|[^\n]+\|[ \t]*)*)/gm,
        (match) => {
            const lines = match.split("\n").filter(l => l.trim());
            if (lines.length < 2) return match;
            const parseRow = (line: string) =>
                line.trim().split("|").slice(1, -1).map(c => c.trim());
            const headers = parseRow(lines[0]);
            const aligns = parseRow(lines[1]).map(sep => {
                const t = sep.trim();
                if (t.startsWith(":") && t.endsWith(":")) return "center";
                if (t.endsWith(":")) return "right";
                return "left";
            });
            const ths = headers.map((h, i) => `<th style="text-align:${aligns[i]}">${h}</th>`).join("");
            const trs = lines.slice(2).map(line => {
                const cells = parseRow(line);
                const tds = headers.map((_, i) =>
                    `<td style="text-align:${aligns[i] || "left"}">${cells[i] ?? ""}</td>`
                ).join("");
                return `<tr>${tds}</tr>`;
            }).join("");
            return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
        }
    );

    // List items
    s = s.replace(/^[ \t]*[*\-] (.+)$/gm, "<li>$1</li>");
    s = s.replace(/^[ \t]*\d+\. (.+)$/gm, "<li>$1</li>");

    // Process paragraphs (split on blank lines)
    const paras = s.split(/\n{2,}/);
    s = paras
        .map(para => {
            const t = para.trim();
            if (!t) return "";
            if (t.startsWith("\x00CB")) return t;
            if (/^<(h[1-6]|hr|blockquote|pre|li|table)[\s>\/]/.test(t)) {
                return t.startsWith("<li>") ? `<ul>${t}</ul>` : t;
            }
            return `<p>${t.replace(/\n/g, "<br />")}</p>`;
        })
        .filter(Boolean)
        .join("\n");

    // Restore code blocks
    codeBlocks.forEach((block, i) => {
        s = s.replace(new RegExp(`\x00CB${i}\x00`, "g"), block);
    });

    return s;
}
